/**
 * Single source of truth for the POST /auth/refresh call.
 *
 * Two client paths refresh the access token: apiClient.tryRefreshToken (the
 * 401-retry on any authed request) and SessionProvider via
 * session-api.refreshAccessTokenWithStatus (bootstrap + visibility/focus). Before
 * this module each had its OWN copy of the native header-refresh + rotate-persist
 * logic and its OWN (or no) in-flight guard, so on native they could fire
 * concurrently: the backend rotates revoke-before-mint, so the first call revokes
 * the shared Keychain refresh token and the loser then POSTs the now-revoked token,
 * gets a 401, and SessionProvider tears the session down — an unexpected native
 * logout. Routing BOTH through one module-level single-flight here coalesces
 * concurrent callers (across both modules) into ONE network request and ONE
 * rotation. native-oauth-pass Ch.1 review fix (race + DRY).
 *
 * Both refresh paths target the SAME endpoint — auth requests resolve to the
 * supervisor (env.authBaseUrl), which is exactly what apiClient.createUrl('/auth/*')
 * picks too — so unifying them changes no target, only de-duplicates the call.
 */
import { env } from '@/lib/env'
import { isNative } from '@/lib/capacitor'
import { getRefreshTokenSecure, setRefreshTokenSecure } from '@/lib/authStorage'
import { diag } from '@/lib/diag'

const AUTH_BASE_URL = env.authBaseUrl
const REFRESH_TIMEOUT_MS = 10000

export interface RefreshResult {
  accessToken: string | null
  email: string | null
  status?: number
  error?: Error
}

let inFlight: Promise<RefreshResult> | null = null

async function doRefresh(storedToken: string | null): Promise<RefreshResult> {
  const nativeClient = isNative()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`
  // Capacitor iOS: the cross-origin refresh cookie is ITP-blocked, so carry the
  // refresh token in a header read from the Keychain and tag the client so the
  // backend takes the header-refresh path (native-oauth-pass Ch.1).
  let hadKeychainToken = false
  if (nativeClient) {
    headers['X-Client-Platform'] = 'capacitor'
    const refreshToken = await getRefreshTokenSecure()
    if (refreshToken) headers['X-Refresh-Token'] = refreshToken
    hadKeychainToken = Boolean(refreshToken)
  }
  // #5105: on native, a refresh with NO Keychain token can only fall back to the
  // ITP-blocked cookie → 401 → relogin. Record whether the credential was even
  // present (booleans only — never the token).
  diag('auth', 'refresh start', { native: nativeClient, hadKeychainToken, hasStoredToken: Boolean(storedToken) })

  const timeout = new AbortController()
  const timer = setTimeout(() => timeout.abort(), REFRESH_TIMEOUT_MS)
  try {
    const response = await fetch(`${AUTH_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // still send the cookie as the web fallback
      headers,
      cache: 'no-store',
      signal: timeout.signal,
    })

    if (!response.ok) {
      diag('auth', `refresh ${response.status} (failed)`, { native: nativeClient, hadKeychainToken })
      return { accessToken: null, email: null, status: response.status }
    }

    const data = await response.json().catch(() => null)
    const accessToken = typeof data?.accessToken === 'string' ? data.accessToken : null
    const email = typeof data?.email === 'string' ? data.email : null

    // Persist the ROTATED refresh token (revoke-before-mint) to the Keychain
    // BEFORE returning. If the client kept the old token the session would "work
    // once then 401" — see the device check's ≥2 refresh cycles. Native only.
    const rotated = nativeClient && typeof data?.refreshToken === 'string'
    if (rotated) {
      await setRefreshTokenSecure(data.refreshToken)
    }

    diag('auth', `refresh ${response.status} (ok)`, { gotAccessToken: Boolean(accessToken), rotatedKeychain: rotated })
    return { accessToken, email, status: response.status }
  } catch (error) {
    diag('auth', 'refresh threw/aborted', { native: nativeClient, hadKeychainToken })
    return { accessToken: null, email: null, error: error as Error }
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Refresh the access token, coalescing concurrent callers into one request.
 * `storedToken` is the caller's current access token (sent as the Authorization
 * bearer so the backend can identify the session); the refresh credential itself
 * is the HttpOnly cookie (web) or the Keychain refresh token (native).
 */
export function refreshSession(storedToken: string | null): Promise<RefreshResult> {
  if (inFlight) return inFlight
  inFlight = doRefresh(storedToken).finally(() => {
    inFlight = null
  })
  return inFlight
}
